import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePoints } from '../context/PointsContext';
import { useWishlist } from '../context/WishlistContext';
import { API_URL, getFullImageUrl } from '../services/api';
import Toast from '../components/Toast';
import './Products.css';

interface Product {
  _id?: string;
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  inStock: boolean;
  isFeatured?: boolean;
  discount?: number;
  stockQuantity?: number;
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [apiError, setApiError] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const { addToCart } = useCart();
  const { points, pointsToCurrency } = usePoints();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const categoryParam = searchParams.get('category');
  const searchQuery = searchParams.get('search') || '';

  /*
   * Keep the selected category synchronized with the URL.
   */
  useEffect(() => {
    setSelectedCategory(categoryParam || 'all');
  }, [categoryParam]);

  /*
   * Fetch ONLY real products from the live API.
   *
   * IMPORTANT:
   * There is intentionally NO mockProducts fallback here.
   */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setApiError(false);

        console.log('Fetching products from:', `${API_URL}/products`);

        const response = await fetch(`${API_URL}/products`);

        if (!response.ok) {
          throw new Error(
            `Products request failed with status ${response.status}`
          );
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          setProducts(data);
        } else if (data?.products && Array.isArray(data.products)) {
          setProducts(data.products);
        } else {
          console.error('Unexpected products API response:', data);
          setProducts([]);
          setApiError(true);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);

        // Do NOT use hardcoded/mock products.
        setProducts([]);
        setApiError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  /*
   * Build category list from REAL products returned by the API.
   */
  const categories = [
    'all',
    ...Array.from(
      new Set(
        products
          .map((product) => product.category)
          .filter(Boolean)
      )
    ),
  ];

  /*
   * First filter by category.
   */
  const categoryFilteredProducts =
    selectedCategory === 'all'
      ? products
      : products.filter(
          (product) => product.category === selectedCategory
        );

  /*
   * Then filter by the search term from:
   * /products?search=shoe
   *
   * Search checks:
   * - product name
   * - description
   * - category
   */
  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredProducts = normalizedSearch
    ? categoryFilteredProducts.filter((product) => {
        const name = product.name?.toLowerCase() || '';
        const description = product.description?.toLowerCase() || '';
        const category = product.category?.toLowerCase() || '';

        return (
          name.includes(normalizedSearch) ||
          description.includes(normalizedSearch) ||
          category.includes(normalizedSearch)
        );
      })
    : categoryFilteredProducts;

  /*
   * Category navigation.
   *
   * We update the URL so category selection is shareable
   * and works correctly with browser navigation.
   */
  const handleCategoryChange = (category: string) => {
    const params = new URLSearchParams(searchParams);

    if (category === 'all') {
      params.delete('category');
    } else {
      params.set('category', category);
    }

    navigate(`/products?${params.toString()}`);
  };

  /*
   * Add product to cart.
   */
  const handleAddToCart = (
    product: Product,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    const productId = product._id || product.id;

    addToCart({
      cartItemId: productId,
      productId: productId,
      id: productId,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1,
      category: product.category,
    });

    setToast({
      message: 'Added to cart!',
      type: 'success',
    });
  };

  /*
   * Wishlist toggle.
   */
  const handleWishlistToggle = (
    e: React.MouseEvent,
    product: Product
  ) => {
    e.stopPropagation();

    const productId = product._id || product.id;

    if (isInWishlist(productId)) {
      removeFromWishlist(productId);

      setToast({
        message: 'Removed from wishlist',
        type: 'info',
      });
    } else {
      addToWishlist({
        _id: productId,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category,
      });

      setToast({
        message: 'Added to wishlist',
        type: 'success',
      });
    }
  };

  /*
   * Open product details.
   */
  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  /*
   * Loading state.
   */
  if (loading) {
    return (
      <div className="products-loading">
        Loading products...
      </div>
    );
  }

  /*
   * API failure state.
   *
   * We deliberately do NOT substitute hardcoded products.
   */
  if (apiError) {
    return (
      <div className="products-page">
        <div className="products-header">
          <h1>Our Products</h1>
          <p>Discover our collection of quality products</p>
        </div>

        <div className="no-products">
          <p>
            We couldn't load the products right now.
          </p>

          <button
            className="add-to-cart-btn"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-header">
        {normalizedSearch ? (
          <>
            <h1>Search Results</h1>
            <p>
              {filteredProducts.length} product
              {filteredProducts.length !== 1 ? 's' : ''} found for "
              {searchQuery}"
            </p>
          </>
        ) : (
          <>
            <h1>Our Products</h1>
            <p>Discover our collection of quality products</p>
          </>
        )}
      </div>

      <div className="points-banner">
        <span>💰 Reward Points: {points}</span>
        <span>
          💎 Value: ₦{pointsToCurrency(points).toFixed(2)}
        </span>
      </div>

      <div className="category-filter">
        {categories.map((category) => (
          <button
            key={category}
            className={`category-btn ${
              selectedCategory === category ? 'active' : ''
            }`}
            onClick={() => handleCategoryChange(category)}
          >
            {category === 'all' ? 'All Products' : category}
          </button>
        ))}
      </div>

      <div className="products-grid">
        {filteredProducts.map((product) => {
          const productId = product._id || product.id;
          const inWishlist = isInWishlist(productId);

          return (
            <div
              key={productId}
              className="product-card"
              onClick={() => handleProductClick(productId)}
              style={{ cursor: 'pointer' }}
            >
              <div className="product-image-container">
                <img
                  src={getFullImageUrl(product.image)}
                  alt={product.name}
                  className="product-image"
                  onError={(e) => {
                    (
                      e.target as HTMLImageElement
                    ).src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"%3E%3Crect width="300" height="300" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />

                <button
                  className={`wishlist-btn ${
                    inWishlist ? 'active' : ''
                  }`}
                  onClick={(e) =>
                    handleWishlistToggle(e, product)
                  }
                  title={
                    inWishlist
                      ? 'Remove from wishlist'
                      : 'Add to wishlist'
                  }
                >
                  {inWishlist ? '❤️' : '🤍'}
                </button>

                {product.discount &&
                product.discount > 0 ? (
                  <span className="discount-badge">
                    -{product.discount}%
                  </span>
                ) : null}
              </div>

              <div className="product-info">
                <h3>
                  {product.name?.length > 20
                    ? `${product.name.substring(0, 20)}......`
                    : product.name}
                </h3>

                <div className="product-price-row">
                  {product.discount &&
                  product.discount > 0 ? (
                    <>
                      <span className="original-price">
                        ₦{product.price.toLocaleString()}
                      </span>

                      <span className="sale-price">
                        ₦
                        {(
                          product.price *
                          (1 - product.discount / 100)
                        ).toLocaleString()}
                      </span>
                    </>
                  ) : (
                    <span className="price">
                      ₦{product.price.toLocaleString()}
                    </span>
                  )}
                </div>

                <button
                  className="add-to-cart-btn"
                  onClick={(e) =>
                    handleAddToCart(product, e)
                  }
                  disabled={!product.inStock}
                >
                  {product.inStock
                    ? 'Add to Cart 🛒︎'
                    : 'Out of Stock'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredProducts.length === 0 && !apiError && (
        <div className="no-products">
          {normalizedSearch ? (
            <>
              <p>
                No products found for "{searchQuery}".
              </p>

              <button
                className="add-to-cart-btn"
                onClick={() => navigate('/products')}
              >
                View All Products
              </button>
            </>
          ) : (
            <p>
              No products are currently available.
            </p>
          )}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Products;